import { useReactiveVar } from "@apollo/client";
import { user as userVar } from "apollo/cache";
import { GET_USER_PROFILE, GET_USER_PROFILE_PACKAGE } from "apollo/queries";
import useImperativeQuery from "hook/useImperativeQuery";
import { useCallback, useMemo, useState } from "react";
import {
  NO_LIMIT_OF_USAGE,
  WARNING_BEFORE_LIMIT_OF_USAGE_LV_1,
  WARNING_BEFORE_LIMIT_OF_USAGE_LV_2,
} from "utility/constants";
import palette from "theme/palette";
import { Auth } from "aws-amplify";

export function useProfile() {
  const user = useReactiveVar(userVar);
  const getUserProfile = useImperativeQuery(GET_USER_PROFILE);
  const getUserProfilePackage = useImperativeQuery(GET_USER_PROFILE_PACKAGE);

  const refreshUserProfile = async () =>
    new Promise((resolve, reject) => {
      getUserProfile()
        .then(({ data }) => {
          userVar({
            ...user,
            ...data.getUser,
          });
          resolve(data);
        })
        .catch((err) => {
          Auth.signOut();
          reject(err);
        })
        .finally(() => {});
    });

  /** package */
  const refreshUserProfilePackage = async () =>
    new Promise((resolve, reject) => {
      getUserProfilePackage()
        .then(({ data }) => {
          userVar({
            ...user,
            ...data.getUser,
          });
          resolve(data);
        })
        .catch((err) => {
          Auth.signOut();
          reject(err);
        })
        .finally(() => {});
    });

  const isUnlimitOfUsage = useMemo(() => {
    return user.package.maxUsage === NO_LIMIT_OF_USAGE;
  }, [user]);

  const remainingUsage = useMemo(() => {
    return user.package.maxUsage - user.package.currentUsage;
  }, [user]);

  const isWarningBeforelimitOfUsage = useMemo(() => {
    if (isUnlimitOfUsage) return false;
    return remainingUsage <= WARNING_BEFORE_LIMIT_OF_USAGE_LV_1;
  }, [remainingUsage, isUnlimitOfUsage]);

  const colorWarningUsage = useCallback(() => {
    if (
      user.package.maxUsage === NO_LIMIT_OF_USAGE ||
      WARNING_BEFORE_LIMIT_OF_USAGE_LV_1 < remainingUsage
    ) {
      return { bar: palette.primary.main, label: palette.primary.main };
    } else if (
      WARNING_BEFORE_LIMIT_OF_USAGE_LV_2 < remainingUsage &&
      remainingUsage <= WARNING_BEFORE_LIMIT_OF_USAGE_LV_1
    ) {
      return { bar: palette.warning.main, label: "#D49B08" };
    } else if (WARNING_BEFORE_LIMIT_OF_USAGE_LV_2 >= remainingUsage) {
      return { bar: palette.error.main, label: palette.error.content };
    }
  }, [remainingUsage, user.package.maxUsage]);

  const isExpired = useMemo(
    () => user.package.status === "inactive" && user.package.type !== "upfront",
    [user.package.status]
  );

  const colorLabelPackage = useCallback(() => {
    switch (user.package.label) {
      case "normal":
        return "#2057A7";
      case "pro":
        return "#4B9752";
      case "enterprise":
          return "#4B9752";
      case "trial":
        return "#ECAA00";
      default:
        return "";
    }
  }, [user]);

  const nameLabelPackage = useCallback(() => {
    switch (user.package.label) {
      case "normal":
        return "M0";
      case "pro":
        return "M0 Pro";
      case "enterprise":
        return "M0 enterprise";
      case "trial":
        return "M0 Trial";
      default:
        return "";
    }
  }, [user]);

  const isEnterprise = useMemo(
    () => user.package.label === "enterprise",
    [user.package.label]
  );

  /** */

  return {
    user,
    userPackage: {
      ...user.package,
      isUnlimitOfUsage,
      isWarningBeforelimitOfUsage,
      colorWarningUsage,
      colorLabelPackage,
      nameLabelPackage,
      remainingUsage,
      isExpired,
      isEnterprise
    },
    refreshUserProfile,
    refreshUserProfilePackage,
  };
}

export default useProfile;
